/**
 * Le catalogue des Moves EN BASE (`move_catalog` + `move_cards`, migration 0752), édité dans l'admin
 * (`/tutor/admin/moves`). L'app le lit au démarrage d'une feuille et le pose dans le catalogue du code
 * (`setMoveCatalog`) : tant que la base n'a pas répondu — ou si elle échoue —, c'est le catalogue livré
 * avec l'app qui sert.
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { SECONDARY_SCHOOL_LEVELS, type SchoolLevel } from '@jeraisonne/db';
import { supabase } from '@/lib/supabase';
import {
  setMoveCatalog,
  type MoveDef,
  type MoveGesture,
  type MovePlacement,
  type MoveType,
} from './move-catalog';

// tables postérieures aux types générés
const db = supabase as unknown as SupabaseClient;

const LEVELS = SECONDARY_SCHOOL_LEVELS.map((l) => l.value) as SchoolLevel[];
const GESTURES = [
  'commute',
  'substitute',
  'distribute',
  'develop',
  'factor',
  'cross',
  'crossmul',
] as const;

const choiceSchema = z.object({
  text: z.string().min(1).max(200),
  ok: z.literal(true).optional(),
  why: z.string().max(400),
});

/** Une ligne de `move_catalog`, telle que la base la renvoie. */
export const moveRowSchema = z.object({
  move_id: z.string().regex(/^[a-z][a-z0-9-]{1,40}$/),
  kind: z.enum(['savoir', 'geste']),
  hora: z.number().int().min(1).max(1000),
  name: z.string().min(1).max(80),
  why: z.string().max(600),
  move_type: z.enum(['interface', 'rewrite', 'equation', 'formula', 'theorem', 'value']),
  gesture: z.enum(GESTURES).nullable(),
  writes: z.string().max(200).nullable(),
  choices: z.array(choiceSchema).max(8),
  min_level: z.string(),
  max_level: z.string().nullable(),
  unlocked_by_default: z.boolean(),
  active: z.boolean(),
  placement: z.enum(['below_line', 'line_right', 'dock', 'full_dock']).nullable(),
  script: z.string().max(60).nullable(),
  position: z.number().int(),
  notes: z.string().max(2000).nullable(),
});
export type MoveRow = z.infer<typeof moveRowSchema>;

export const MOVE_ROW_COLUMNS =
  'move_id, kind, hora, name, why, move_type, gesture, writes, choices, min_level, max_level, unlocked_by_default, active, placement, script, position, notes';

/** Les niveaux d'un Move : de son niveau minimum à son niveau maximum (le dernier du secondaire). PUR. */
export function moveLevels(min: string, max: string | null): SchoolLevel[] {
  const a = LEVELS.indexOf(min as SchoolLevel);
  if (a < 0) return [];
  const b = max ? LEVELS.indexOf(max as SchoolLevel) : LEVELS.length - 1;
  return LEVELS.slice(a, (b < a ? a : b) + 1);
}

/** Une ligne de la base → un Move du catalogue. PUR. */
export function rowToMove(row: MoveRow, cardIds: readonly string[] = []): MoveDef {
  const levels = moveLevels(row.min_level, row.max_level);
  return {
    id: row.move_id,
    kind: row.kind,
    name: row.name,
    why: row.why,
    writes: row.writes ?? undefined,
    gesture: (row.gesture ?? undefined) as MoveGesture | undefined,
    choices: row.choices.map((c) => ({ text: c.text, why: c.why, ...(c.ok ? { ok: true } : {}) })),
    levels,
    hora: row.hora,
    type: row.move_type as MoveType,
    active: row.active,
    unlockedByDefault: row.unlocked_by_default,
    placement: (row.placement ?? null) as MovePlacement | null,
    script: row.script,
    cardIds: [...cardIds],
    position: row.position,
    notes: row.notes ?? '',
  };
}

/** Un Move du catalogue → les colonnes à écrire (sans l'id). PUR. */
export function moveToRow(m: MoveDef): Omit<MoveRow, 'move_id'> {
  return {
    kind: m.kind,
    hora: m.hora,
    name: m.name,
    why: m.why,
    move_type: m.type,
    gesture: m.kind === 'geste' ? (m.gesture ?? null) : null,
    writes: m.kind === 'savoir' ? (m.writes ?? null) : null,
    choices: m.choices.map((c) => ({
      text: c.text,
      why: c.why,
      ...(c.ok ? { ok: true as const } : {}),
    })),
    min_level: m.levels[0] ?? '6e',
    max_level: m.levels.at(-1) ?? null,
    unlocked_by_default: m.unlockedByDefault,
    active: m.active,
    placement: m.kind === 'geste' ? m.placement : null,
    script: m.script,
    position: m.position,
    notes: m.notes || null,
  };
}

/** Lit le catalogue complet. Une ligne mal formée est écartée (le Move livré la remplace). */
export async function fetchMoveCatalog(): Promise<MoveDef[]> {
  const [rows, links] = await Promise.all([
    db.from('move_catalog').select(MOVE_ROW_COLUMNS).order('position'),
    db.from('move_cards').select('move_id, card_id'),
  ]);
  if (rows.error) throw rows.error;
  const cards = new Map<string, string[]>();
  for (const l of (links.data ?? []) as { move_id: string; card_id: string }[])
    cards.set(l.move_id, [...(cards.get(l.move_id) ?? []), l.card_id]);
  const out: MoveDef[] = [];
  for (const raw of rows.data ?? []) {
    const r = moveRowSchema.safeParse(raw);
    if (r.success) out.push(rowToMove(r.data, cards.get(r.data.move_id) ?? []));
  }
  return out;
}

export const MOVE_CATALOG_KEY = ['move-catalog'] as const;

/**
 * Le catalogue de la base, posé dans celui du code dès qu'il arrive. Renvoie un numéro qui change à chaque
 * nouveau catalogue (à mettre dans les dépendances de ce qui lit le catalogue).
 */
export function useMoveCatalog(enabled = true): number {
  const q = useQuery({
    queryKey: MOVE_CATALOG_KEY,
    queryFn: async () => {
      const defs = await fetchMoveCatalog();
      // une base vide (migration pas encore passée) : on garde le catalogue livré
      if (defs.length) setMoveCatalog(defs);
      return defs.length;
    },
    enabled,
    staleTime: 5 * 60_000,
  });
  return q.dataUpdatedAt;
}
